import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProject } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LoadingPage, NotFoundPage } from "@/components/PageStates";
import { ArrowLeft, Calendar, Tag, Users, Clock, DollarSign, BarChart3 } from "lucide-react";
import DOMPurify from "dompurify";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getProject(id).then(r => setProject(r.data)).catch(() => setProject(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingPage />;
  if (!project) return <NotFoundPage title="Projet introuvable" message="Ce projet n'est plus disponible ou a été supprimé." backLabel="Retour aux projets" backTo="/#projects" />;

  return (
    <div data-testid="project-detail-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <div className="bg-gradient-to-br from-[#1670B9] via-[#2d8fd4] to-[#74CDD9] pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/#projects" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Retour aux projets</Link>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="px-3 py-1 rounded-lg bg-white/20 text-white text-xs font-semibold">{project.category}</span>
            {project.project_status && <span className="px-3 py-1 rounded-lg bg-[#F47A00]/80 text-white text-xs font-semibold">{project.project_status}</span>}
            {project.created_at && <span className="flex items-center gap-1 text-xs text-white/60"><Calendar className="w-3 h-3" />{new Date(project.created_at).toLocaleDateString("fr-FR")}</span>}
          </div>
          <h1 className="font-['Quicksand'] text-3xl sm:text-4xl font-medium text-white mb-4">{project.title}</h1>
          <p className="text-base text-white/80 max-w-3xl">{project.summary}</p>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 space-y-10">
        {(project.duration || project.funding || project.partners?.length > 0) && (
          <div className="grid sm:grid-cols-3 gap-4">
            {project.duration && (
              <div className="bg-white rounded-xl p-5 border border-slate-100">
                <Clock className="w-5 h-5 text-[#1670B9] mb-2" /><p className="text-xs text-slate-400 uppercase tracking-wider">Durée</p><p className="text-sm font-medium text-slate-800 mt-1">{project.duration}</p>
              </div>
            )}
            {project.funding && (
              <div className="bg-white rounded-xl p-5 border border-slate-100">
                <DollarSign className="w-5 h-5 text-[#F47A00] mb-2" /><p className="text-xs text-slate-400 uppercase tracking-wider">Financement</p><p className="text-sm font-medium text-slate-800 mt-1">{project.funding}</p>
              </div>
            )}
            {project.partners?.length > 0 && (
              <div className="bg-white rounded-xl p-5 border border-slate-100">
                <Users className="w-5 h-5 text-[#57D0D9] mb-2" /><p className="text-xs text-slate-400 uppercase tracking-wider">Partenaires</p>
                <div className="flex flex-wrap gap-1.5 mt-1">{project.partners.map((p, i) => <span key={`p-${p}-${i}`} className="px-2 py-0.5 rounded bg-slate-100 text-xs text-slate-600">{p}</span>)}</div>
              </div>
            )}
          </div>
        )}
        {project.image_url && <img src={project.image_url} alt={project.title} className="w-full h-64 md:h-80 object-cover rounded-xl shadow-sm" />}
        {project.content && (
          <section>
            <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-4">Description détaillée</h2>
            <div className="bg-white rounded-xl p-8 border border-slate-100 prose prose-slate max-w-none [&_img]:rounded-xl" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(project.content) }} />
          </section>
        )}
        {project.results && (
          <section>
            <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#F47A00]" /> Résultats</h2>
            <div className="bg-gradient-to-r from-[#F47A00]/5 to-transparent rounded-xl p-6 border-l-4 border-[#F47A00]">
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(project.results) }} />
            </div>
          </section>
        )}
        {project.tags?.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">{project.tags.map((t, i) => <span key={`tag-${t}-${i}`} className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-[#0F62B3]/10 text-[#0F62B3] text-xs font-medium"><Tag className="w-3 h-3" />{t}</span>)}</div>
        )}
      </div>
      <Footer />
    </div>
  );
}
